import { useEffect, useState } from "react";
import { Platform } from "react-native";
import * as AuthSession from "expo-auth-session";
import * as WebBrowser from "expo-web-browser";
import * as SecureStore from "expo-secure-store";
import { useAuth } from "../../context/Auth/AuthContext";
import { setBearerToken } from "../../services/TimeTracking/api";
import { getUserIdByEmail } from "../../services/TimeTracking/api";

WebBrowser.maybeCompleteAuthSession(); // For iOS closing auth window

const isWeb = Platform.OS === "web";

const redirectUri = AuthSession.makeRedirectUri({
  useProxy: !isWeb, // use proxy on native, direct redirect on web
} as AuthSession.AuthSessionRedirectUriOptions);

const discovery = {
  authorizationEndpoint: `https://${process.env.EXPO_PUBLIC_AUTH0_DOMAIN}/authorize`,
  tokenEndpoint: `https://${process.env.EXPO_PUBLIC_AUTH0_DOMAIN}/oauth/token`,
  revocationEndpoint: `https://${process.env.EXPO_PUBLIC_AUTH0_DOMAIN}/oauth/revoke`,
};

const TOKEN_KEY = "auth_token";
const USER_KEY = "user_info";

// Cross-platform storage wrappers
const storeItem = async (key: string, value: string) => {
  if (isWeb) {
    localStorage.setItem(key, value);
  } else {
    await SecureStore.setItemAsync(key, value);
  }
};

const getItem = async (key: string): Promise<string | null> => {
  return isWeb
    ? localStorage.getItem(key)
    : await SecureStore.getItemAsync(key);
};

const deleteItem = async (key: string) => {
  if (isWeb) {
    localStorage.removeItem(key);
  } else {
    await SecureStore.deleteItemAsync(key);
  }
};

export const useAuth0 = () => {
  
  const { userInfo, setUserInfo } = useAuth();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [request, response, promptAsync] = AuthSession.useAuthRequest(
    {
      clientId: process.env.EXPO_PUBLIC_AUTH0_CLIENT_ID!,
      redirectUri,
      scopes: process.env.EXPO_PUBLIC_AUTH0_SCOPE.split(","),
      responseType: AuthSession.ResponseType.Code,
      usePKCE: true,
      extraParams: {
        audience: process.env.EXPO_PUBLIC_AUTH0_AUDIENCE, // 👈 THIS MATTERS
      },
    },
    discovery
  );

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      const token = await getItem(TOKEN_KEY);
      const userData = await getItem(USER_KEY);

      if (token && userData) {
        setBearerToken(token); // Set the bearer token in your API service
        setAccessToken(token);
        setUserInfo(JSON.parse(userData));
      }
    };

    restoreSession();
  }, []);

  // Handle login response
  useEffect(() => {
    const handleAuthResponse = async () => {
      if (response?.type === "success" && response.params.code) {
        try {
          const tokenResponse = await AuthSession.exchangeCodeAsync(
            {
              clientId: process.env.EXPO_PUBLIC_AUTH0_CLIENT_ID!,
              code: response.params.code,
              redirectUri,
              extraParams: {
                code_verifier: request?.codeVerifier || "",
                audience: process.env.EXPO_PUBLIC_AUTH0_AUDIENCE,
              },
            },
            discovery
          );

          const token = tokenResponse.accessToken;
          setAccessToken(token);
          await storeItem(TOKEN_KEY, token);
          setBearerToken(token);

          const user = await fetchUserInfo(token);
          console.log("👤 Auth0 User Info:", user);

          if (user && user.email) {
            console.log("📧 Fetching appUserId for:", user.email);

            const appUser = await getUserIdByEmail(user.email);

            if (appUser?.id) {
              const userWithAppId = {
                ...user,
                appUserId: appUser.id,
                photo: appUser.photo,
                manager: appUser.manager
              };

              setUserInfo(userWithAppId);
              await storeItem(USER_KEY, JSON.stringify(userWithAppId));
              console.log("✅ Stored user with appUserId:", userWithAppId);
            } else {
              console.warn("⚠️ No user ID returned from getUserIdByEmail");
            }
          } else {
            console.warn("⚠️ No email found in user data.");
          }
        } catch (err) {
          console.error("❌ Token exchange failed:", err);
        }
      }
    };

    handleAuthResponse();
  }, [response]);

  const loginWithAuth0 = async () => {
    const result = await promptAsync();
    if (result.type !== "success") {
      console.warn("❌ Auth0 login failed or cancelled");
      return null;
    }
    // useEffect handles success
  };

  const logoutFromAuth0 = async () => {
    const logoutUrl = `https://${process.env.EXPO_PUBLIC_AUTH0_DOMAIN}/v2/logout?client_id=${process.env.EXPO_PUBLIC_AUTH0_CLIENT_ID}&returnTo=${redirectUri}`;

    setAccessToken(null);
    setUserInfo(null);

    await deleteItem(TOKEN_KEY);
    await deleteItem(USER_KEY);

    await WebBrowser.openAuthSessionAsync(logoutUrl, redirectUri);
  };

  const fetchUserInfo = async (token: string) => {
    const res = await fetch(
      `https://${process.env.EXPO_PUBLIC_AUTH0_DOMAIN}/userinfo`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await res.json();
    return data;
  };

  const isTokenExpired = async (): Promise<boolean> => {
    const token = await getItem(TOKEN_KEY);
    if (!token) return true;
  
    try {
      const jwtPayload = JSON.parse(atob(token.split(".")[1]));
      const now = Math.floor(Date.now() / 1000);
      return jwtPayload.exp < now;
    } catch (error) {
      console.warn("❌ Error parsing JWT:", error);
      return true;
    }
  };

  return {
    loginWithAuth0,
    logoutFromAuth0,
    accessToken,
    userInfo,
    isTokenExpired
  };
};
